import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Card, message, Spin } from 'antd';
import { Breadcrumbs } from '../../components/layout/Breadcrumbs';
import { ShopPromotionForm } from '../../components/shop-promotions/ShopPromotionForm';
import {
  createShopPromotion,
  fetchShopPromotionById,
  updateShopPromotion,
} from '../../services/shopPromotionsService';
import type { ShopPromotionRecord } from '../../types/shopPromotion';

export default function ShopPromotionFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [initial, setInitial] = useState<ShopPromotionRecord | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const numId = parseInt(id, 10);
    if (!Number.isFinite(numId)) {
      setError('ID không hợp lệ');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchShopPromotionById(numId);
      if (res.success && res.data) {
        setInitial(res.data);
        setError(null);
      } else {
        setError(res.message || 'Không tìm thấy');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) load();
  }, [isEdit, load]);

  const handleSubmit = async (body: Record<string, unknown>) => {
    try {
      if (isEdit && id) {
        const res = await updateShopPromotion(parseInt(id, 10), body);
        if (res.success) {
          message.success('Đã cập nhật khuyến mãi');
          navigate(`/shop-promotions/${id}`);
        } else {
          message.error(res.message || 'Cập nhật thất bại');
        }
      } else {
        const res = await createShopPromotion(body);
        if (res.success && res.data?.id) {
          message.success('Đã tạo khuyến mãi');
          navigate(`/shop-promotions/${res.data.id}`);
        } else {
          message.error(res.message || 'Tạo thất bại');
        }
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string; errors?: unknown[] } };
      };
      const msg =
        ax.response?.data?.message ||
        (Array.isArray(ax.response?.data?.errors)
          ? 'Dữ liệu không hợp lệ'
          : 'Lỗi lưu khuyến mãi');
      message.error(msg);
      throw e;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs
        items={[
          { label: 'Trang chủ', path: '/' },
          { label: 'KM quán', path: '/shop-promotions' },
          {
            label: isEdit ? 'Sửa khuyến mãi' : 'Thêm khuyến mãi',
          },
        ]}
      />
      <h2 style={{ marginBottom: 16 }}>
        {isEdit ? 'Sửa khuyến mãi quán' : 'Thêm khuyến mãi quán'}
      </h2>
      <Card style={{ maxWidth: 720 }}>
        <ShopPromotionForm
          mode={isEdit ? 'edit' : 'create'}
          initial={initial}
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate(
              isEdit && id ? `/shop-promotions/${id}` : '/shop-promotions'
            )
          }
        />
      </Card>
    </div>
  );
}
